import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Chip, Tooltip, InputBase, Button } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import SearchIcon from '@material-ui/icons/Search';
import { showNotification } from '../../App';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import {
    retrieveEnquirySourceData, mapActionTypeOptions, LeadActionData, ActionTypeOption,
} from './ActionTypeRadio';

// Explicit request (v2, reference: Followup.png) - the cap is derived from these shared
// constants (also exposed as the --chip-h/--gap/--pad CSS custom properties the chips
// themselves use - see root below), not a value guessed separately for JS vs CSS. Chip height/
// gap/padding changing only ever needs to change here.
const VISIBLE_ROW_COUNT = 3;
const CHIP_HEIGHT_PX = 30;
const GAP_PX = 6;
const PAD_PX = 6;
const THREE_ROW_CONTENT_HEIGHT_PX = (VISIBLE_ROW_COUNT * CHIP_HEIGHT_PX) + ((VISIBLE_ROW_COUNT - 1) * GAP_PX);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            '--chip-h': `${CHIP_HEIGHT_PX}px`,
            '--gap': `${GAP_PX}px`,
            '--pad': `${PAD_PX}px`,
        },
        // the action groups after the LOG row (create email, book a meeting...): the same column as the root, so the spacing does not change
        actionGroups: {
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
        },
        groupRow: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
        },
        groupLabelColumn: {
            flexShrink: 0,
            width: 92,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            paddingTop: 7,
        },
        groupLabel: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: theme.palette.text.secondary,
        },
        chipRowWrapper: {
            position: 'relative',
            flex: 1,
            minWidth: 0,
        },
        // Explicit bug report: the chip list fetches its options async (retrieveEnquirySourceData)
        // and rendered nothing at all until that resolved, so the whole section appeared to pop
        // in out of nowhere. Mirrors the real groupRow/chipRow layout (pill-shaped shape
        // skeletons standing in for chips) so the swap-in doesn't jump the layout around.
        skeletonChipRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--gap)',
        },
        // Explicit request: a group whose chips overflow VISIBLE_ROW_COUNT rows gets a frame
        // around BOTH the toolbar and the chip area below it, marking the whole thing as one
        // distinct, scrollable unit - groups that fit get neither this frame nor the toolbar.
        groupFrame: {
            border: '1px solid #E2E7EE',
            borderRadius: 8,
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 8px',
            borderBottom: '1px solid #E2E7EE',
        },
        searchInput: {
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            backgroundColor: '#F6F7F9',
            borderRadius: 6,
            padding: '4px 8px',
            '& .MuiInputBase-input': {
                padding: 0,
            },
        },
        searchIcon: {
            fontSize: 15,
            marginRight: 5,
            color: theme.palette.text.disabled,
            flexShrink: 0,
        },
        toolbarMeta: {
            flexShrink: 0,
            fontSize: 11.5,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            '& b': {
                color: theme.palette.text.primary,
                fontWeight: 700,
            },
        },
        showAllButton: {
            flexShrink: 0,
            minWidth: 0,
            padding: '2px 6px',
            fontSize: 11.5,
            fontWeight: 700,
            textTransform: 'none',
        },
        chipRow: {
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'flex-start',
            gap: 'var(--gap)',
        },
        // Applied whenever the chip row sits inside groupFrame, whether or not it's currently
        // capped (show-all included) - keeps chips off the frame's own border either way.
        chipRowFramed: {
            padding: 'var(--pad) 8px',
        },
        // Explicit request: max-height comes straight from the same --chip-h/--gap/--pad
        // custom properties the chips themselves are styled with (see chip/chipRow below), so
        // it can never drift out of sync with their real rendered size the way a separately
        // guessed pixel constant did.
        chipRowCapped: {
            boxSizing: 'border-box',
            maxHeight: `calc(${VISIBLE_ROW_COUNT} * var(--chip-h) + ${VISIBLE_ROW_COUNT - 1} * var(--gap) + 2 * var(--pad))`,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
                width: 6,
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.25)',
                borderRadius: 3,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
        },
        // Positioned off-screen (not display:none, which would drop it from layout entirely) -
        // mirrors the real row's own width/gap/chip metrics so its natural scrollHeight tells
        // us whether the FULL, unfiltered option list overflows VISIBLE_ROW_COUNT rows, without
        // that measurement ever being skewed by a search filter hiding some chips.
        measureRow: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            visibility: 'hidden',
            pointerEvents: 'none',
        },
        emptyState: {
            padding: '14px 8px',
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            textAlign: 'center',
        },
        chip: {
            height: 'var(--chip-h)',
            minHeight: 'var(--chip-h)',
            maxHeight: 'var(--chip-h)',
            borderRadius: 999,
            border: '1px solid #CDD4DF',
            backgroundColor: '#fff',
            fontSize: 12.5,
            '& .MuiChip-label': {
                padding: '0 10px',
            },
        },
        chipSelected: {
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            borderColor: 'var(--rv-accent-indigo)',
            color: 'var(--rv-accent-indigo)',
            fontWeight: 700,
            '& $chipCheckIcon': {
                color: 'var(--rv-accent-indigo)',
            },
        },
        chipCheckIcon: {
            fontSize: 16,
        },
    }),
);

interface FollowUpActionChipsProps {
    value?: string;
    onSelect: (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean,
        systemName: string,
        systemAction: string,
        url: string | null,
        defaultEmailTemplate?: IAutoCompleteItem | null,
    ) => void;
}

interface ActionChipGroup {
    key: string;
    label?: string;
    options: ActionTypeOption[];
    sortSequence: number;
}

// Explicit request (reference: Followup.png) - the Follow Up Action list is grouped by its own
// systemAction value (sequence-ordered within and across groups), not a fixed set of categories,
// since the options themselves are tenant-configurable (fetched from the backend, see
// ActionTypeRadio.tsx) - an option with no systemAction gets its own ungrouped chip rather than
// being forced into a group or dropped.
const buildActionChipGroups = (options: ActionTypeOption[]): ActionChipGroup[] => {
    const groupsBySystemAction = new Map<string, ActionChipGroup>();
    const ungroupedOptions: ActionTypeOption[] = [];

    options.forEach((option) => {
        const sequence = Number(option.sequence) || 0;
        if (option.systemAction) {
            const existingGroup = groupsBySystemAction.get(option.systemAction);
            if (existingGroup) {
                existingGroup.options.push(option);
                existingGroup.sortSequence = Math.min(existingGroup.sortSequence, sequence);
            } else {
                groupsBySystemAction.set(option.systemAction, {
                    key: option.systemAction,
                    label: humanizeSystemAction(option.systemAction),
                    options: [option],
                    sortSequence: sequence,
                });
            }
        } else {
            ungroupedOptions.push(option);
        }
    });

    const labeledGroups = Array.from(groupsBySystemAction.values());
    labeledGroups.forEach((group) => group.options.sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0)));
    labeledGroups.sort((a, b) => a.sortSequence - b.sortSequence);

    if (ungroupedOptions.length === 0) {
        return labeledGroups;
    }

    // Explicit request: options with no systemAction all sit together in one row at the top,
    // not scattered through the labelled groups by sequence. Explicit follow-up request: this
    // row gets a fallback "LOG" label (same all-caps convention as humanizeSystemAction's own
    // output) instead of sitting unlabelled.
    ungroupedOptions.sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0));
    const ungroupedGroup: ActionChipGroup = {
        key: 'ungrouped',
        label: 'LOG',
        options: ungroupedOptions,
        sortSequence: Number.NEGATIVE_INFINITY,
    };
    return [ungroupedGroup].concat(labeledGroups);
};

const humanizeSystemAction = (systemAction: string): string => (
    systemAction
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim()
        .toUpperCase()
);

// For the search placeholder/empty-state only - "CREATE EMAIL" -> "create email" reads as a
// normal sentence fragment ("Search 15 create email options…") where the all-caps group label
// itself would read as shouting.
const toPlaceholderCase = (label: string): string => label.toLowerCase();

interface ChipGroupRowProps {
    group: ActionChipGroup;
    selectedValue?: string;
    onChipClick: (option: ActionTypeOption) => void;
}

// Extracted so each group measures/filters/caps its own chip row independently - a group's own
// chip count changing (or the selected value moving into a different group) shouldn't force
// every other group to remeasure or lose its own search term.
const ChipGroupRow: React.FC<ChipGroupRowProps> = ({ group, selectedValue, onChipClick }) => {
    const classes = useStyles();
    const measureRowRef = useRef<HTMLSpanElement>(null);
    const rowRef = useRef<HTMLSpanElement>(null);
    const selectedChipRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isShowAll, setIsShowAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [hiddenCount, setHiddenCount] = useState(0);

    // Explicit request (v2, reference: Followup.png): whether a group overflows is measured
    // against THREE_ROW_CONTENT_HEIGHT_PX - the same constants the CSS max-height calc uses -
    // on the hidden measureRow (always the FULL, unfiltered option list) so a search filter
    // narrowing the visible list never makes an overflowing group look like it doesn't.
    const measureIsOverflowing = () => {
        if (measureRowRef.current) {
            setIsOverflowing(measureRowRef.current.scrollHeight > THREE_ROW_CONTENT_HEIGHT_PX + 1);
        }
    };

    useLayoutEffect(() => {
        measureIsOverflowing();
    // tslint:disable-next-line: align
    }, [group.options]);

    useEffect(() => {
        if (typeof ResizeObserver === 'undefined' || !measureRowRef.current) {
            return undefined;
        }
        // Re-measure on width changes (narrow/wide layout, Quick View docking) - the chips
        // re-wrap, so whether the group overflows VISIBLE_ROW_COUNT rows can change too.
        const observer = new ResizeObserver(() => measureIsOverflowing());
        observer.observe(measureRowRef.current);
        return () => observer.disconnect();
    // tslint:disable-next-line: align
    }, [group.options]);

    useEffect(() => {
        // Web fonts swapping in after first paint can change chip widths/wrapping - re-measure
        // once they're ready rather than leaving the overflow decision based on a fallback-font
        // layout.
        // tslint:disable-next-line: no-any
        const fontSet = (document as any).fonts;
        if (fontSet && typeof fontSet.ready?.then === 'function') {
            fontSet.ready.then(() => measureIsOverflowing());
        }
    // tslint:disable-next-line: align
    }, []);

    useEffect(() => {
        if (selectedChipRef.current) {
            selectedChipRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    // tslint:disable-next-line: align
    }, [selectedValue]);

    // Explicit request: a currently selected chip always stays visible regardless of the
    // filter, so picking an action then typing a search term can't hide the user's own choice.
    const trimmedSearchTerm = searchTerm.trim().toLowerCase();
    const filteredOptions = trimmedSearchTerm
        ? group.options.filter((option) => option.label.toLowerCase().includes(trimmedSearchTerm) || option.label === selectedValue)
        : group.options;

    const showToolbarAndFrame = isOverflowing || !!trimmedSearchTerm;
    const isCapped = showToolbarAndFrame && !isShowAll;

    const computeHiddenCount = (): number => {
        if (!rowRef.current) {
            return 0;
        }
        const rowRect = rowRef.current.getBoundingClientRect();
        const clientHeight = rowRef.current.clientHeight;
        return Array.from(rowRef.current.children).reduce((count, child) => {
            const rect = (child as HTMLElement).getBoundingClientRect();
            return (rect.bottom - rowRect.top > clientHeight + 1) ? count + 1 : count;
        }, 0);
    };

    useLayoutEffect(() => {
        setHiddenCount(isCapped ? computeHiddenCount() : 0);
    // tslint:disable-next-line: align
    }, [isCapped, filteredOptions.length, group.key]);

    // Explicit request: reset to the top whenever the search term changes, rather than leaving
    // the scroll position wherever it was for the previous (unrelated) set of matches.
    useEffect(() => {
        if (rowRef.current) {
            rowRef.current.scrollTop = 0;
        }
    // tslint:disable-next-line: align
    }, [trimmedSearchTerm]);

    const onChipRowScroll = () => {
        setHiddenCount(computeHiddenCount());
    };

    const renderChip = (option: ActionTypeOption, forMeasurement: boolean) => {
        const isSelected = option.label === selectedValue;
        const chip = (
            <Chip
                key={option.value}
                label={option.label}
                size="small"
                clickable={!forMeasurement}
                variant="outlined"
                icon={isSelected ? <CheckIcon className={classes.chipCheckIcon} /> : undefined}
                className={isSelected ? `${classes.chip} ${classes.chipSelected}` : classes.chip}
                onClick={forMeasurement ? undefined : () => onChipClick(option)}
            />
        );
        const wrappedChip = option.notes ? (
            <Tooltip key={option.value} title={option.notes} arrow={true}>
                {chip}
            </Tooltip>
        ) : chip;
        return forMeasurement ? (
            <span key={option.value}>{wrappedChip}</span>
        ) : (
            <span key={option.value} ref={isSelected ? selectedChipRef : undefined}>
                {wrappedChip}
            </span>
        );
    };

    const placeholderGroupName = toPlaceholderCase(group.label || 'follow up');
    let metaText: React.ReactNode;
    if (trimmedSearchTerm) {
        metaText = `${filteredOptions.length} of ${group.options.length} match`;
    } else if (hiddenCount > 0) {
        metaText = <><b>{hiddenCount} more below</b> · {group.options.length} total</>;
    } else {
        metaText = `${group.options.length} total`;
    }

    return (
        <div className={classes.groupRow} data-tour={group.key === 'ungrouped' ? 'enquiry-log-activity' : undefined}>
            {group.label && (
                <span className={classes.groupLabelColumn}>
                    <span className={classes.groupLabel}>{group.label}</span>
                </span>
            )}
            <span className={classes.chipRowWrapper}>
                <span ref={measureRowRef} className={clsx(classes.chipRow, classes.measureRow)}>
                    {group.options.map((option) => renderChip(option, true))}
                </span>
                {showToolbarAndFrame ? (
                    <div className={classes.groupFrame}>
                        <div className={classes.toolbar}>
                            <InputBase
                                className={classes.searchInput}
                                placeholder={`Search ${group.options.length} ${placeholderGroupName} options…`}
                                startAdornment={<SearchIcon className={classes.searchIcon} />}
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                inputProps={{ autoComplete: 'abcd' }}
                            />
                            <span className={classes.toolbarMeta}>{metaText}</span>
                            <Button
                                size="small"
                                className={classes.showAllButton}
                                onClick={() => setIsShowAll((current) => !current)}
                            >
                                {isShowAll ? 'Show fewer' : 'Show all'}
                            </Button>
                        </div>
                        {filteredOptions.length === 0 ? (
                            <div className={classes.emptyState}>No options match &ldquo;{searchTerm.trim()}&rdquo;.</div>
                        ) : (
                            <span
                                ref={rowRef}
                                className={clsx(classes.chipRow, classes.chipRowFramed, isCapped && classes.chipRowCapped)}
                                onScroll={isCapped ? onChipRowScroll : undefined}
                            >
                                {filteredOptions.map((option) => renderChip(option, false))}
                            </span>
                        )}
                    </div>
                ) : (
                    <span ref={rowRef} className={classes.chipRow}>
                        {group.options.map((option) => renderChip(option, false))}
                    </span>
                )}
            </span>
        </div>
    );
};

// Explicit bug report: FollowUpActionChips fetches its options async and rendered nothing
// until that resolved, so the section appeared to pop in out of nowhere. A plain, fixed stand-
// in (3 group rows, a handful of pill widths per row) rather than measuring/matching the real
// eventual group count - the real content's own group/chip counts are exactly what we don't
// know yet while this is showing.
const FollowUpActionChipsSkeleton: React.FC = () => {
    const classes = useStyles();
    const skeletonRows = [3, 4, 2];
    return (
        <div className={classes.root}>
            {skeletonRows.map((chipCount, rowIndex) => (
                <div key={rowIndex} className={classes.groupRow}>
                    <span className={classes.groupLabelColumn}>
                        <SkeletonLoader variant="shape" shape="text" width={60} height={11} />
                    </span>
                    <span className={classes.skeletonChipRow}>
                        {Array.from({ length: chipCount }).map((_, chipIndex) => (
                            <SkeletonLoader
                                key={chipIndex}
                                variant="shape"
                                shape="rect"
                                width={70 + (chipIndex * 18)}
                                height={CHIP_HEIGHT_PX}
                                style={{ borderRadius: 999 }}
                            />
                        ))}
                    </span>
                </div>
            ))}
        </div>
    );
};

export const FollowUpActionChips: React.FC<FollowUpActionChipsProps> = (props) => {
    const [options, setOptions] = useState<ActionTypeOption[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);

    useEffect(() => {
        retrieveEnquirySourceData(
            false,
            (data: LeadActionData) => {
                setOptions(mapActionTypeOptions(data));
                setIsLoadingOptions(false);
            },
            // tslint:disable-next-line:no-any
            (reason: any) => {
                showNotification(null, reason, 'error');
                setIsLoadingOptions(false);
            },
        );
    // tslint:disable-next-line: align
    }, []);

    const classes = useStyles();

    if (isLoadingOptions) {
        return <FollowUpActionChipsSkeleton />;
    }

    const groups = buildActionChipGroups(options);

    const onChipClick = (option: ActionTypeOption) => {
        // ActionTypeRadio's own onRadioButtonChange only ever reads event.currentTarget.value
        // (the option's label) - a minimal synthetic event carrying just that keeps this a
        // drop-in replacement for the same onFollowUpChange handler, with no changes there.
        const syntheticEvent = {
            currentTarget: { value: option.label },
        } as React.ChangeEvent<HTMLInputElement>;
        const defaultEmailTemplate = option.emailTemplateGuid
            ? { value: option.emailTemplateGuid, label: option.emailTemplateName || '' }
            : null;
        props.onSelect(syntheticEvent, true, option.secondaryValue, option.tertiaryValue, option.url, defaultEmailTemplate);
    };

    return (
        <div className={classes.root}>
            {groups.filter((group) => group.key === 'ungrouped').map((group) => (
                <ChipGroupRow
                    key={group.key}
                    group={group}
                    selectedValue={props.value}
                    onChipClick={onChipClick}
                />
            ))}
            {groups.some((group) => group.key !== 'ungrouped') && (
                <div className={classes.actionGroups} data-tour="enquiry-do-something">
                    {groups.filter((group) => group.key !== 'ungrouped').map((group) => (
                        <ChipGroupRow
                            key={group.key}
                            group={group}
                            selectedValue={props.value}
                            onChipClick={onChipClick}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
